import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiTreeActivity, apiTreeLogQuick, apiTreeGet, FOLLOWUP_METHODS } from "../followuptree/api";
import { _apiGet } from "../../lib/apiClient";
import { CustomIcon } from "../shared/CustomIcon";
import EmptyState from "../shared/EmptyState";

// -----------------------------------------------------------------------------
// Iter-57 — Log Entry page (replaces the legacy archive).
// Two sections: Visitors + Members. Each has a "Log Follow-Up" gold button
// that opens a shared LogFollowUpModal. Real-time search filters filter the
// list as the user types.
// -----------------------------------------------------------------------------

function LogEntryRow({ a, m }) {
  return (
    <div data-testid={`log-entry-${a.id}`} style={{ padding: "8px 12px", background: "#111", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, marginBottom: 6 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
        <strong style={{ color: "var(--kc-text)", fontSize: 13 }}>{a.memberName}</strong>
        {a.memberNumber && <span style={{ color: "var(--kc-accent)", fontSize: 11 }}>#{a.memberNumber}</span>}
        {a.memberLevelName && <span style={{ fontSize: 10, color: "var(--kc-sub)", border: "1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent)", borderRadius: 999, padding: "1px 6px" }}>{a.memberLevelName}</span>}
        <span style={{ marginLeft: "auto", color: "var(--kc-sub)", fontSize: 11 }}>{a.date} · {a.method}</span>
      </div>
      {a.notes && <div style={{ fontSize: 12, color: "var(--kc-sub)", marginTop: 4, whiteSpace: "pre-wrap" }}>{a.notes}</div>}
      <div style={{ fontSize: 10, color: "var(--kc-meta)", marginTop: 4 }}>
        by {a.loggedByName || "Someone"}{m?.memberStatus ? ` · status: ${m.memberStatus}` : ""}
      </div>
    </div>
  );
}

function LogFollowUpModal({ kind, candidates, currentUser, users, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [memberId, setMemberId] = useState("");
  const [date, setDate] = useState(today);
  const [method, setMethod] = useState(FOLLOWUP_METHODS[0]);
  const [notes, setNotes] = useState("");
  const [loggingAs, setLoggingAs] = useState(currentUser?.id || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isSA = currentUser?.role === "superadmin";

  const save = async () => {
    if (!memberId) { setErr("Please choose a person."); return; }
    setBusy(true); setErr("");
    try {
      const body = { memberId, date, method, notes: notes || null };
      if (isSA && loggingAs && loggingAs !== currentUser.id) body.loggedByUserId = loggingAs;
      const r = await apiTreeLogQuick(body);
      onSaved && onSaved(r.activity);
      onClose();
    } catch (e) {
      setErr(e?.message || "Could not save the log entry.");
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="log-followup-modal"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 320, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent)", borderRadius: 14, width: "min(480px, 100%)", maxHeight: "88vh", overflow: "auto" }}
      >
        <div style={{ padding: "16px 18px", borderBottom: "1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent)", display: "flex", alignItems: "center", gap: 8 }}>
          <CustomIcon name={kind === "visitor" ? "person" : "people"} size={16} />
          <div style={{ flex: 1, fontFamily: "'Cinzel',serif", fontSize: 15, color: "var(--kc-accent)", letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700 }}>
            Log Follow-Up · {kind === "visitor" ? "Visitor" : "Member"}
          </div>
          <button data-testid="log-modal-close" onClick={onClose} style={{ background: "transparent", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", color: "var(--kc-accent)", borderRadius: 999, width: 30, height: 30, cursor: "pointer" }}>×</button>
        </div>
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          {isSA && users && users.length > 0 && (
            <div>
              <label style={{ fontSize: 10, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Logging as</label>
              <select value={loggingAs} onChange={e => setLoggingAs(e.target.value)} data-testid="log-modal-logging-as" style={{ width: "100%" }}>
                <option value={currentUser.id}>{currentUser.name || currentUser.username} (me)</option>
                {users.filter(u => u.id !== currentUser.id).map(u => (
                  <option key={u.id} value={u.id}>{u.name || u.username}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label style={{ fontSize: 10, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              {kind === "visitor" ? "Visitor" : "Member"} *
            </label>
            <select value={memberId} onChange={e => setMemberId(e.target.value)} data-testid="log-modal-person" style={{ width: "100%" }}>
              <option value="">— Choose a {kind === "visitor" ? "visitor" : "member"} —</option>
              {candidates.map(c => (
                <option key={c.id} value={c.id}>
                  {c.firstName} {c.lastName}{c.memberNumber ? ` · #${String(c.memberNumber).padStart(7, "0")}` : ""}
                </option>
              ))}
            </select>
            {candidates.length === 0 && (
              <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 4 }}>
                {kind === "visitor" ? "You have no assigned visitors yet." : "Your subtree has no members to log against yet."}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 10, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Date *</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="log-modal-date" style={{ width: "100%" }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 10, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Method *</label>
              <select value={method} onChange={e => setMethod(e.target.value)} data-testid="log-modal-method" style={{ width: "100%" }}>
                {FOLLOWUP_METHODS.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={{ fontSize: 10, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} data-testid="log-modal-notes" rows={3} placeholder="Optional notes…" style={{ width: "100%" }} />
          </div>
          {err && <div style={{ fontSize: 12, color: "#ff6b6b" }} data-testid="log-modal-error">{err}</div>}
        </div>
        <div style={{ padding: "12px 18px", borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-gold" data-testid="log-modal-save" onClick={save} disabled={busy || !memberId}>
            {busy ? "Saving…" : "Save Log"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LogEntryPage({ currentUser }) {
  const navigate = useNavigate();
  const [activity, setActivity] = useState([]);
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [tree, setTree] = useState(null);
  const [qVisitor, setQVisitor] = useState("");
  const [qMember, setQMember] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [openModal, setOpenModal] = useState(null); // "visitor" | "member" | null

  const isSA = currentUser?.role === "superadmin";
  const isAdmin = isSA || currentUser?.role === "limited_admin";

  const reload = async () => {
    try {
      const [a, m, t] = await Promise.all([
        apiTreeActivity(500, 0),
        _apiGet("/members"),
        apiTreeGet().catch(() => null),
      ]);
      setActivity(a.items || []);
      setMembers(Array.isArray(m) ? m : []);
      setTree(t);
      if (isSA) {
        try { setUsers(await _apiGet("/users")); } catch (_) { /* ignore */ }
      }
    } catch (_) { /* ignore */ }
  };
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const memMap = useMemo(() => {
    const map = {};
    members.forEach(m => { if (m?.id) map[m.id] = m; });
    return map;
  }, [members]);

  // Candidate lists for the Log Follow-Up modal.
  //  - Visitors: viewer's assignedMembers filtered to memberStatus=Visitor
  //    (Admins see every Visitor).
  //  - Members: everyone in the viewer's subtree (Admins see every non-visitor).
  const visitorCandidates = useMemo(() => {
    const all = members.filter(m => m?.memberStatus === "Visitor");
    if (isAdmin) return all;
    const assigned = new Set(currentUser?.assignedMembers || []);
    return all.filter(m => assigned.has(m.id));
  }, [members, isAdmin, currentUser]);

  const memberCandidates = useMemo(() => {
    const nonVisitors = members.filter(m => m?.memberStatus && !["Visitor", "Guest"].includes(m.memberStatus));
    if (isAdmin) return nonVisitors;
    // Non-admin: filter to their subtree via the tree structure.
    if (!tree?.nodes || !currentUser?.memberId) return [];
    const myNode = Object.values(tree.nodes).find(n => n.memberId === currentUser.memberId);
    if (!myNode) return [];
    const subtreeMemberIds = new Set();
    const walk = (nid) => {
      const n = tree.nodes[nid]; if (!n) return;
      if (n.memberId) subtreeMemberIds.add(n.memberId);
      (n.childNodeIds || []).forEach(walk);
    };
    walk(myNode.nodeId);
    return nonVisitors.filter(m => subtreeMemberIds.has(m.id));
  }, [members, tree, isAdmin, currentUser]);

  const inRange = (dateStr) => {
    if (from && dateStr < from) return false;
    if (to && dateStr > to) return false;
    return true;
  };

  const visitorEntries = useMemo(() => activity.filter(a => {
    const m = memMap[a.memberId];
    if (!m || m.memberStatus !== "Visitor") return false;
    const q = qVisitor.trim().toLowerCase();
    if (q && !(a.memberName || "").toLowerCase().includes(q) && !(a.notes || "").toLowerCase().includes(q)) return false;
    return inRange(a.date || "");
  }), [activity, memMap, qVisitor, from, to]);

  const memberEntries = useMemo(() => activity.filter(a => {
    const m = memMap[a.memberId];
    if (!m) return false;
    if (m.memberStatus === "Visitor" || m.memberStatus === "Guest") return false;
    const q = qMember.trim().toLowerCase();
    if (q) {
      const num = String(a.memberNumber || "").replace(/^#/, "");
      const paddedNum = /^\d+$/.test(num) ? num.padStart(7, "0") : num;
      const numQ = q.replace(/^#/, "");
      const hit = (a.memberName || "").toLowerCase().includes(q)
        || paddedNum.includes(numQ)
        || (a.notes || "").toLowerCase().includes(q);
      if (!hit) return false;
    }
    return inRange(a.date || "");
  }), [activity, memMap, qMember, from, to]);

  const handleSaved = (entry) => {
    setActivity(prev => [entry, ...prev]);
  };

  return (
    <div className="page-container" data-testid="log-entry-page">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <button className="btn btn-sm btn-outline" onClick={() => navigate("/followuptree")}>← Back</button>
        <div style={{ fontFamily: "'Cinzel',serif", fontSize: 20, color: "var(--kc-accent)", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase" }}>Log Entry</div>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "end" }}>
        <div>
          <label style={{ fontSize: 10, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.08em" }}>From</label>
          <input type="date" data-testid="log-from" value={from} onChange={e => setFrom(e.target.value)} className="form-input" style={{ maxWidth: 150 }} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.08em" }}>To</label>
          <input type="date" data-testid="log-to" value={to} onChange={e => setTo(e.target.value)} className="form-input" style={{ maxWidth: 150 }} />
        </div>
      </div>

      <section data-testid="log-section-visitors" style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          <h3 style={{ flex: "1 1 auto", fontFamily: "'Cinzel',serif", fontSize: 15, color: "#ff6a00", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 8, margin: 0 }}>
            <CustomIcon name="person" size={16} /> Visitors <span style={{ fontSize: 10, color: "var(--kc-sub)" }}>({visitorEntries.length})</span>
          </h3>
          <button className="btn btn-gold btn-sm" data-testid="log-followup-visitor-btn" onClick={() => setOpenModal("visitor")}>
            <CustomIcon name="plus" size={12} /> Log Follow-Up
          </button>
        </div>
        <input type="text" data-testid="log-visitor-search" placeholder="Search visitor name or notes…" value={qVisitor} onChange={e => setQVisitor(e.target.value)} className="form-input" style={{ marginBottom: 8 }} />
        {visitorEntries.length === 0
          ? <EmptyState
              data-testid="log-visitor-empty"
              icon="celebrate"
              title="No visitor follow-up entries"
              subtitle="Log a follow-up from a visitor interaction to see it here."
            />
          : visitorEntries.map(a => <LogEntryRow key={a.id} a={a} m={memMap[a.memberId]} />)}
      </section>

      <section data-testid="log-section-members">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          <h3 style={{ flex: "1 1 auto", fontFamily: "'Cinzel',serif", fontSize: 15, color: "#39ff14", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 8, margin: 0 }}>
            <CustomIcon name="people" size={16} /> Members <span style={{ fontSize: 10, color: "var(--kc-sub)" }}>({memberEntries.length})</span>
          </h3>
          <button className="btn btn-gold btn-sm" data-testid="log-followup-member-btn" onClick={() => setOpenModal("member")}>
            <CustomIcon name="plus" size={12} /> Log Follow-Up
          </button>
        </div>
        <input type="text" data-testid="log-member-search" placeholder="Search name, member number, or notes…" value={qMember} onChange={e => setQMember(e.target.value)} className="form-input" style={{ marginBottom: 8 }} />
        {memberEntries.length === 0
          ? <EmptyState
              data-testid="log-member-empty"
              icon="people"
              title="No member follow-up entries"
              subtitle="Log a follow-up from a member interaction to see it here."
            />
          : memberEntries.map(a => <LogEntryRow key={a.id} a={a} m={memMap[a.memberId]} />)}
      </section>

      {openModal && (
        <LogFollowUpModal
          kind={openModal}
          candidates={openModal === "visitor" ? visitorCandidates : memberCandidates}
          currentUser={currentUser}
          users={isSA ? users : []}
          onClose={() => setOpenModal(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
